import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Trash2, Globe, MapPin, Building2, Users,
  Briefcase, Loader2, ChevronRight, FileText, CreditCard, Landmark
} from "lucide-react";
import {
  COMPANY_STATUSES, COMPANY_STATUS_LABELS, COMPANY_STATUS_COLORS
} from "../lib/constants";

const TABS = [
  { id: "overview", label: "Overview", icon: Building2 },
  { id: "legal", label: "Legal", icon: FileText },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "bank", label: "Bank", icon: Landmark },
];

export default function CompanyDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [cRes, contactsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contacts?company_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/deals?company_id=${id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCompany(cRes.data);
      setForm(cRes.data);
      setContacts(contactsRes.data);
      setDeals(dealsRes.data);
    } catch { toast.error("Failed to load company"); navigate("/companies"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id: _id, contacts_count, deals_count, created_at, updated_at, ...payload } = form;
      const res = await axios.put(`${API}/companies/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setCompany(res.data);
      setEditing(false);
      toast.success("Company updated");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this company? This won't delete linked contacts or deals.")) return;
    try {
      await axios.delete(`${API}/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Company deleted");
      navigate("/companies");
    } catch { toast.error("Failed to delete"); }
  };

  const Field = ({ label, field, type = "text", placeholder = "" }) => {
    const val = editing ? (form[field] || "") : (company[field] || "");
    if (!editing && !val) return null;
    return (
      <div>
        <label className="form-label">{label}</label>
        {editing ? (
          type === "textarea" ? (
            <textarea value={val} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="form-input min-h-[80px]" placeholder={placeholder} />
          ) : (
            <input type={type} value={val} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="form-input" placeholder={placeholder} />
          )
        ) : (
          <p className="text-white/70 text-sm">{val}</p>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;
  if (!company) return null;

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/companies" className="p-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center shrink-0 overflow-hidden">
          {company.logo ? <img src={company.logo} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-5 h-5 text-white/30" />}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-2xl text-white" data-testid="company-detail-name">{company.company_name}</h1>
          <p className="text-white/40 text-sm">{[company.city, company.country].filter(Boolean).join(", ")}{company.industry ? ` · ${company.industry}` : ""}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setForm(company); }} className="px-4 py-2 text-sm text-white/50 hover:text-white bg-white/5 rounded-lg">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-accent flex items-center gap-2 text-sm" data-testid="save-company-detail-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="px-4 py-2 text-sm text-white bg-white/5 hover:bg-white/10 rounded-lg" data-testid="edit-company-btn">Edit</button>
              <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl w-fit" data-testid="company-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${activeTab === tab.id ? "bg-[#C9A96E]/15 text-[#C9A96E]" : "text-white/40 hover:text-white/70"}`} data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="glass-card-static p-6 space-y-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Company Details</h3>
              <Field label="Company Name" field="company_name" />
              {/* Company Lifecycle Status */}
              <div>
                <label className="form-label">Partner Status</label>
                {editing ? (
                  <select value={form.status || "discovered"} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="form-input" data-testid="company-status-select">
                    {COMPANY_STATUSES.map(s => <option key={s} value={s}>{COMPANY_STATUS_LABELS[s]}</option>)}
                  </select>
                ) : (
                  <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${COMPANY_STATUS_COLORS[company.status] || COMPANY_STATUS_COLORS.discovered}`} data-testid="company-status-badge">
                    {COMPANY_STATUS_LABELS[company.status] || company.status || "Discovered"}
                  </span>
                )}
              </div>
              <Field label="Website" field="website" placeholder="https://..." />
              <div className="grid grid-cols-2 gap-4">
                <Field label="City" field="city" />
                <Field label="Country" field="country" />
              </div>
              <Field label="Industry" field="industry" />
              <Field label="Logo URL" field="logo" />
              <Field label="Notes" field="notes" type="textarea" />
              {editing && (
                <>
                  <h3 className="text-white/50 text-xs uppercase tracking-wider mt-6 mb-2">Branding</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Primary Color" field="brand_primary_color" placeholder="#C9A96E" />
                    <Field label="Secondary Color" field="brand_secondary_color" placeholder="#C9A96E" />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Legal Tab */}
          {activeTab === "legal" && (
            <div className="glass-card-static p-6 space-y-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Legal Information</h3>
              <Field label="Legal Company Name" field="legal_company_name" />
              <Field label="Legal Form" field="legal_form" placeholder="e.g. GmbH, AG, Ltd" />
              <Field label="Managing Director" field="managing_director" />
              <Field label="Registration Court" field="registration_court" />
              <Field label="Commercial Register No." field="commercial_register_number" />
              <Field label="VAT ID" field="vat_id" placeholder="e.g. DE123456789" />
              <Field label="Tax ID" field="tax_id" />
              {!editing && !company.legal_company_name && !company.vat_id && (
                <p className="text-white/30 text-sm py-4">No legal information added yet. Click Edit to add.</p>
              )}
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === "billing" && (
            <div className="glass-card-static p-6 space-y-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Billing Information</h3>
              <Field label="Billing Address" field="billing_address" type="textarea" />
              <Field label="Billing Email" field="billing_email" type="email" />
              <Field label="Payment Terms" field="payment_terms" placeholder="e.g. Net 30" />
              <Field label="Currency" field="currency" placeholder="EUR" />
              {!editing && !company.billing_address && !company.billing_email && (
                <p className="text-white/30 text-sm py-4">No billing information added yet. Click Edit to add.</p>
              )}
            </div>
          )}

          {/* Bank Tab */}
          {activeTab === "bank" && (
            <div className="glass-card-static p-6 space-y-4">
              <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2">Bank Information</h3>
              <Field label="Bank Name" field="bank_name" />
              <Field label="IBAN" field="iban" />
              <Field label="BIC" field="bic" />
              <Field label="Account Holder" field="account_holder" />
              {!editing && !company.bank_name && !company.iban && (
                <p className="text-white/30 text-sm py-4">No bank information added yet. Click Edit to add.</p>
              )}
            </div>
          )}
        </div>

        {/* Sidebar: Contacts & Deals */}
        <div className="space-y-6">
          <div className="glass-card-static p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white/50 text-xs uppercase tracking-wider">Contacts ({contacts.length})</h3>
              <Link to="/contacts" className="text-[#C9A96E] text-xs hover:underline">View All</Link>
            </div>
            {contacts.length === 0 ? <p className="text-white/30 text-sm">No contacts linked</p> : (
              <div className="space-y-2">
                {contacts.slice(0, 5).map(c => (
                  <Link key={c.id} to={`/contacts/${c.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.04] transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#C9A96E]/15 flex items-center justify-center text-[#C9A96E] text-xs font-semibold">{c.first_name?.[0]}{c.last_name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{c.full_name}</p>
                      <p className="text-white/40 text-xs truncate">{c.role}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="glass-card-static p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white/50 text-xs uppercase tracking-wider">Deals ({deals.length})</h3>
              <Link to="/deals" className="text-[#C9A96E] text-xs hover:underline">View All</Link>
            </div>
            {deals.length === 0 ? <p className="text-white/30 text-sm">No deals yet</p> : (
              <div className="space-y-2">
                {deals.slice(0, 5).map(d => (
                  <Link key={d.id} to={`/deals/${d.id}`} className="block p-3 bg-white/[0.03] rounded-lg hover:bg-white/[0.06] transition-colors">
                    <p className="text-white text-sm font-medium truncate">{d.title}</p>
                    <p className="text-white/40 text-xs">{d.status_label} {d.value_estimate > 0 ? `· €${d.value_estimate.toLocaleString()}` : ""}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
